import Papa from 'papaparse';
import { useCallback, useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Design, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { awaitJSON, StreamingUtilsLambdaFetch } from '../../../utils/fetch-utils';
import { conditionallyPluralizeWithCount } from '../../../utils/text-utils';
import { AdOpsToolProps } from '../adops-constants';
import { ImportModal } from './ImportModal';
import { normalizeAdServer } from './streaming-mapping-utils';

const Dial = {
  MAPPING: 'MAPPING',
  IGNORE: 'IGNORE'
} as const;
type DialType = (typeof Dial)[keyof typeof Dial];

const DIAL_OPTIONS: string[] = [Dial.MAPPING, Dial.IGNORE];

const DERIVED_ID_PATTERN = '(BPM[A-Z0-9]+ID[0-9]+)';
const ISCI_PATTERN = '([A-Z]{2}[A-Za-z0-9]{2,}?ENH)';
const DISPLAY_PATTERN = '(.*?[0-9]{2,4}x[0-9]{2,4}.*)';

interface Mapping {
  bpm_id: string;
  partner_id: string;
  source: string;
}

interface Ignore {
  source: string;
  name: string;
  type: string;
}

const patternMatch = (input: string, pattern: string): string => {
  const match = input.trim().match(new RegExp(pattern));
  return match ? match[1] : '';
};

const addToMap = (map: Map<string, string[]>, key: string, value: string) => {
  const existing = map.get(key);
  map.set(key, existing && existing.length ? [...existing, value.trim()] : [value.trim()]);
};

const addToDerivedIdMap = (
  map: Map<string, { derivedId: string; partnerIds: string[]; adServer: string }>,
  derivedId: string,
  partnerId: string,
  adServer: string
) => {
  const key = `${derivedId} ${adServer}`;
  const existing = map.get(key);
  map.set(key, {
    derivedId,
    partnerIds: existing ? [...existing.partnerIds, partnerId.trim()] : [partnerId.trim()],
    adServer
  });
};

const processMappingRows = (
  rows: Mapping[],
  derivedIdMappings: Map<string, { derivedId: string; partnerIds: string[]; adServer: string }>,
  isciMappings: Map<string, string[]>,
  failedMappings: string[]
): {
  tempDerivedIdMappings: Map<string, { derivedId: string; partnerIds: string[]; adServer: string }>;
  tempIsciMappings: Map<string, string[]>;
  tempFailedMappings: string[];
} => {
  const tempDerivedIdMappings = new Map(derivedIdMappings);
  const tempIsciMappings = new Map(isciMappings);
  const tempFailedMappings = [...failedMappings];
  rows.forEach(row => {
    if (!row.bpm_id && !row.partner_id) {
      return;
    }
    if (!row.bpm_id || !row.partner_id || !row.source) {
      tempFailedMappings.push(`${row.source} - ${row.bpm_id}: ${row.partner_id}`);
      return;
    }
    const derivedId = patternMatch(String(row.bpm_id), DERIVED_ID_PATTERN);
    const isci = patternMatch(String(row.bpm_id), ISCI_PATTERN);
    const displayId = patternMatch(String(row.bpm_id), DISPLAY_PATTERN);
    if (derivedId) {
      const adServer = normalizeAdServer(String(row.source));
      if (!adServer) {
        tempFailedMappings.push(`${row.source} - ${row.bpm_id}: ${row.partner_id}`);
        return;
      }
      addToDerivedIdMap(tempDerivedIdMappings, derivedId, String(row.partner_id), adServer);
    } else if (isci) {
      addToMap(tempIsciMappings, isci, String(row.partner_id));
    } else if (displayId) {
      addToMap(tempIsciMappings, displayId, String(row.partner_id));
    } else {
      tempFailedMappings.push(`${row.source} - ${row.bpm_id}: ${row.partner_id}`);
    }
  });
  return {
    tempDerivedIdMappings,
    tempIsciMappings,
    tempFailedMappings
  };
};

const processIgnoreRows = (
  rows: Ignore[],
  lineItemIgnores: Map<string, string[]>,
  isciIgnores: Map<string, string[]>,
  failedIgnores: string[]
): {
  tempLineItemIgnores: Map<string, string[]>;
  tempIsciIgnores: Map<string, string[]>;
  tempFailedIgnores: string[];
} => {
  const tempLineItemIgnores = new Map(lineItemIgnores);
  const tempIsciIgnores = new Map(isciIgnores);
  const tempFailedIgnores = [...failedIgnores];
  rows.forEach(row => {
    if (!row.source && !row.name && !row.type) {
      return;
    }
    if (!row.source || !row.name || !row.type) {
      tempFailedIgnores.push(`${row.source} - ${row.type} - ${row.name}`);
      return;
    }
    const rawSource = String(row.source).trim().toUpperCase();
    const source = rawSource === 'WMT' ? 'TTD' : rawSource;
    const type = String(row.type).trim().toUpperCase();
    if (type === 'ISCI') {
      addToMap(tempIsciIgnores, source, String(row.name));
    } else {
      addToMap(tempLineItemIgnores, source, String(row.name));
    }
  });
  return {
    tempLineItemIgnores,
    tempIsciIgnores,
    tempFailedIgnores
  };
};

export const StreamingMappingAdder = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [dial, setDial] = useState<DialType | null>(null);
  const { setError } = utils;
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [file, setFile] = useState<File | undefined>();
  const [derivedIdMappings, setDerivedIdMappings] = useState<
    Map<string, { derivedId: string; partnerIds: string[]; adServer: string }>
  >(new Map());
  const [isciMappings, setIsciMappings] = useState<Map<string, string[]>>(new Map());
  const [failedMappings, setFailedMappings] = useState<string[]>([]);
  const [lineItemIgnores, setLineItemIgnores] = useState<Map<string, string[]>>(new Map());
  const [isciIgnores, setIsciIgnores] = useState<Map<string, string[]>>(new Map());
  const [failedIgnores, setFailedIgnores] = useState<string[]>([]);

  const addStreamingMapping = useCallback(async () => {
    try {
      setActiveAPICall(true);
      const mappingRes = await StreamingUtilsLambdaFetch('/streamingMappingAdder', {
        method: 'POST',
        body: {
          derivedIdMappings: Object.fromEntries(derivedIdMappings),
          isciMappings: Object.fromEntries(isciMappings),
          failedMappings
        }
      });
      const { failedPartnerIds, ambiguousIsci, ambiguousDerivedId, ambiguousSimilarPlacements } =
        await awaitJSON<{
          failedPartnerIds: string[];
          ambiguousIsci: string[];
          ambiguousDerivedId: string[];
          ambiguousSimilarPlacements: string[];
        }>(mappingRes);
      if (failedMappings.length) {
        setError({
          message: `The following mappings could not be parsed. All other mappings successfully submitted. \n ${failedMappings.join(
            ', '
          )}`,
          reportError: new Error('Failed mappings')
        });
      }
      if (failedPartnerIds.length) {
        setError({
          message: `${conditionallyPluralizeWithCount(failedPartnerIds.length, 'partner ID')} could not be mapped due to ambiguous or missing records — check #ad_ops_alerts for details.`,
          reportError: new Error('Failed mappings')
        });
      }
      const ambiguousDetails = [
        ...ambiguousDerivedId,
        ...ambiguousSimilarPlacements,
        ...ambiguousIsci
      ];
      if (ambiguousDetails.length) {
        setError({
          message: `The following entries need manual review before they can be mapped: ${ambiguousDetails.join('; ')}`,
          reportError: new Error('Ambiguous mappings')
        });
      }
      setDerivedIdMappings(new Map());
      setIsciMappings(new Map());
      setFailedMappings([]);
      setFile(undefined);
      setDial(null);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [derivedIdMappings, isciMappings, failedMappings, setError]);

  const addStreamingIgnore = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await StreamingUtilsLambdaFetch('/streamingIgnoreAdder', {
        method: 'POST',
        body: {
          lineItemIgnores: Object.fromEntries(lineItemIgnores),
          isciIgnores: Object.fromEntries(isciIgnores),
          failedIgnores
        }
      });
      if (failedIgnores.length) {
        setError({
          message: `The following ignored placements could not be parsed. All other ignores successfully entered. \n ${failedIgnores.join(
            ', '
          )}`,
          reportError: new Error('Failed ignores')
        });
      }
      setLineItemIgnores(new Map());
      setIsciIgnores(new Map());
      setFailedIgnores([]);
      setFile(undefined);
      setDial(null);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [lineItemIgnores, isciIgnores, failedIgnores, setError]);

  const importMappingData = useCallback(async () => {
    if (!file) {
      return;
    }
    try {
      const text = await file.text();
      if (dial === Dial.MAPPING) {
        const { data: rows } = Papa.parse<Mapping>(text, { header: true, skipEmptyLines: true });
        const { tempDerivedIdMappings, tempIsciMappings, tempFailedMappings } = processMappingRows(
          rows,
          derivedIdMappings,
          isciMappings,
          failedMappings
        );
        setDerivedIdMappings(tempDerivedIdMappings);
        setIsciMappings(tempIsciMappings);
        setFailedMappings(tempFailedMappings);
      } else if (dial === Dial.IGNORE) {
        const { data: rows } = Papa.parse<Ignore>(text, { header: true, skipEmptyLines: true });
        const { tempLineItemIgnores, tempIsciIgnores, tempFailedIgnores } = processIgnoreRows(
          rows,
          lineItemIgnores,
          isciIgnores,
          failedIgnores
        );
        setLineItemIgnores(tempLineItemIgnores);
        setIsciIgnores(tempIsciIgnores);
        setFailedIgnores(tempFailedIgnores);
      }
    } catch (e) {
      setError({
        message: `Failed to import: ${e.message}`,
        reportError: e
      });
    }
  }, [
    dial,
    file,
    setError,
    derivedIdMappings,
    isciMappings,
    failedMappings,
    lineItemIgnores,
    isciIgnores,
    failedIgnores
  ]);

  useEffect(() => {
    if (failedMappings.length && !derivedIdMappings.size && !isciMappings.size && !activeAPICall) {
      setError({
        message:
          'All provided mappings could not be parsed. Please double check your file structure. Columns should be: bpm_id, partner_id, source',
        reportError: new Error('All provided mappings could not be parsed')
      });
    }
  }, [setError, failedMappings, derivedIdMappings, isciMappings, activeAPICall]);

  useEffect(() => {
    if (failedIgnores.length && !lineItemIgnores.size && !isciIgnores.size && !activeAPICall) {
      setError({
        message:
          'All provided ignores could not be parsed. Please double check your file structure. Columns should be: source, type, name',
        reportError: new Error('All provided ignores could not be parsed')
      });
    }
  }, [setError, failedIgnores, lineItemIgnores, isciIgnores, activeAPICall]);

  return (
    <Card className='ad-ops-card' data-testid='streaming-mapping'>
      <div className='ad-ops-card-title'>Streaming Mappings</div>
      <div className='ad-ops-card-subtitle'>
        Select from Mapping/Ignore dropdown prior to importing
      </div>
      <div className='ad-ops-elem'>
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.LG}
          placeholder='Select Mapping or Ignore'

          value={dial || ''}
          options={DIAL_OPTIONS}
          onChange={val => {
            if (val === Dial.MAPPING) {
              setLineItemIgnores(new Map());
              setIsciIgnores(new Map());
              setFailedIgnores([]);
              setFile(undefined);
              setDial(Dial.MAPPING);
            } else if (val === Dial.IGNORE) {
              setDerivedIdMappings(new Map());
              setIsciMappings(new Map());
              setFailedMappings([]);
              setFile(undefined);
              setDial(Dial.IGNORE);
            } else {
              setDial(null);
            }
          }}
        />
      </div>
      <div className='ad-ops-elem'>
        <Button
          size={Size.LG}
          design={Design.NEUTRAL}
          disabled={dial === null}
          onClick={() => setShowBulkImportModal(true)}
          data-testid='streaming-mapping-import'
        >
          Import
        </Button>
      </div>
      <div className='ad-ops-elem'>
        <Button
          size={Size.LG}
          loading={activeAPICall}
          disabled={
            dial === null ||
            (dial === Dial.MAPPING && !derivedIdMappings.size && !isciMappings.size) ||
            (dial === Dial.IGNORE && !lineItemIgnores.size && !isciIgnores.size)
          }
          onClick={dial === Dial.MAPPING ? addStreamingMapping : addStreamingIgnore}
          data-testid='streaming-mapping-submit'
        >
          Submit
        </Button>
      </div>
      {showBulkImportModal && (
        <ImportModal
          importFunction={importMappingData}
          file={file}
          setFile={selectedFile => {
            if (!selectedFile || !selectedFile.name.endsWith('.csv')) {
              setError({
                message: 'File must be a .csv',
                reportError: new Error('File must be a .csv')
              });
            } else {
              setFile(selectedFile);
            }
          }}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}
    </Card>
  );
};
